import { extendTheme } from '@chakra-ui/react';

import { breakpoints } from './tokens/breakpoints';
import { colors } from './tokens/colors';
import { fonts } from './tokens/typography';
import { letterSpacings } from './tokens/typography';
import { lineHeights } from './tokens/typography';
import { shadows } from './tokens/shadows';
import { sizes } from './tokens/sizes';
import { spacing } from './tokens/spacing';
import { navScrollbar } from './tokens/scrollbars';

import { accordionTheme } from './components/Accordion';
import { alertTheme } from './components/Alert';
import { avatarTheme } from './components/Avatar';
import { badgeTheme } from './components/Badge';
import { breadcrumbTheme } from './components/Breadcrumb';
import { buttonTheme } from './components/Button';
import { checkboxTheme } from './components/Checkbox';
import { drawerTheme } from './components/Drawer';
import { formLabelTheme } from './components/FormLabel';
import { headingTheme } from './components/Heading';
import { inputTheme } from './components/Input';
import { linkTheme } from './components/Link';
import { listTheme } from './components/List';
import { menuTheme } from './components/Menu';
import { modalTheme } from './components/Modal';
import { numberInputTheme } from './components/NumberInput';
import { popoverTheme } from './components/Popover';
import { radioTheme } from './components/Radio';
import { selectTheme } from './components/Select';
import { tableTheme } from './components/Table';
import { tabsTheme } from './components/Tabs';
import { tagTheme } from './components/Tag';
import { textareaTheme } from './components/Textarea';
import { textTheme } from './components/Text';
import { tooltipTheme } from './components/Tooltip';

export const enrolyTheme = extendTheme({
  config: {
    cssVarPrefix: 'ds'
  },

  breakpoints: breakpoints,
  colors: colors,
  fonts: fonts,
  letterSpacings: letterSpacings,
  lineHeights: lineHeights,
  shadows: shadows,
  sizes: sizes,
  space: spacing,

  semanticTokens: {
    colors: {
      'chakra-body-text': { _light: 'utility.900', _dark: 'whiteAlpha.800' },
      'chakra-border-color': { _light: 'gray.200', _dark: 'gray.600' },
      'chakra-body-bg': { _light: 'white', _dark: 'gray.900' },
      'chakra-subtle-bg': { _light: 'gray.50', _dark: 'gray.800' },

      'zebra-bg': { _light: 'white', _dark: 'gray.900' },
      'hover-bg': { _light: 'gray.100', _dark: 'gray.700' },
      'active-bg': { _light: 'gray.200', _dark: 'gray.600' },
      'selected-bg': { _light: 'blue.50', _dark: 'utility.900' },
      'selected-border': { _light: 'blue.200', _dark: 'whiteAlpha.300' },

      'link-color': { _light: 'blue.600', _dark: 'blue.200' },
      'link-hover-color': { _light: 'blue.700', _dark: 'blue.100' },
      'link-active-color': { _light: 'blue.800', _dark: 'blue.50' },
      'link-visited-color': { _light: 'violet.600', _dark: 'violet.300' },

      error: { _light: 'red.500', _dark: 'red.300' },
      warning: { _light: 'orange.700', _dark: 'orange.300' },
      success: { _light: 'green.500', _dark: 'green.300' },
      info: { _light: 'cyan.500', _dark: 'cyan.300' },
      active: { _light: 'blue.500', _dark: 'blue.300' },
      vivid: { _light: 'pink.500', _dark: 'pink.400' },
      muted: { _light: 'gray.500', _dark: 'whiteAlpha.600' },
      'utility-icon': { _light: 'utility.500', _dark: 'whiteAlpha.600' },
      'extra-muted': { _light: 'utility.300', _dark: 'whiteAlpha.400' },

      insights: {
        primary: {
          label: { _light: 'cyan.500', _dark: 'cyan.300' },
          100: { _light: 'cyan.100', _dark: 'cyan.900' },
          200: { _light: 'cyan.200', _dark: 'cyan.800' },
          300: { _light: 'cyan.300', _dark: 'cyan.700' },
          400: { _light: 'cyan.400', _dark: 'cyan.600' },
          500: { _light: 'cyan.500', _dark: 'cyan.500' },
          600: { _light: 'cyan.600', _dark: 'cyan.400' },
          700: { _light: 'cyan.700', _dark: 'cyan.300' },
          800: { _light: 'cyan.800', _dark: 'cyan.200' },
          900: { _light: 'cyan.900', _dark: 'cyan.100' }
        },
        secondary: {
          label: { _light: 'violet.500', _dark: 'violet.300' },
          100: { _light: 'violet.100', _dark: 'violet.900' },
          200: { _light: 'violet.200', _dark: 'violet.800' },
          300: { _light: 'violet.300', _dark: 'violet.700' },
          400: { _light: 'violet.400', _dark: 'violet.600' },
          500: { _light: 'violet.500', _dark: 'violet.500' },
          600: { _light: 'violet.600', _dark: 'violet.400' },
          700: { _light: 'violet.700', _dark: 'violet.300' },
          800: { _light: 'violet.800', _dark: 'violet.200' },
          900: { _light: 'violet.900', _dark: 'violet.100' }
        },
        industry: {
          label: { _light: 'orange.500', _dark: 'orange.300' },
          100: { _light: 'orange.100', _dark: 'orange.900' },
          200: { _light: 'orange.200', _dark: 'orange.800' },
          300: { _light: 'orange.300', _dark: 'orange.700' },
          400: { _light: 'orange.400', _dark: 'orange.600' },
          500: { _light: 'orange.500', _dark: 'orange.500' },
          600: { _light: 'orange.600', _dark: 'orange.400' },
          700: { _light: 'orange.700', _dark: 'orange.300' },
          800: { _light: 'orange.800', _dark: 'orange.200' },
          900: { _light: 'orange.900', _dark: 'orange.100' }
        },
        increase: {
          100: { _light: 'teal.100', _dark: 'teal.900' },
          200: { _light: 'teal.200', _dark: 'teal.800' },
          300: { _light: 'teal.300', _dark: 'teal.700' },
          400: { _light: 'teal.400', _dark: 'teal.600' },
          500: { _light: 'teal.500', _dark: 'teal.500' },
          600: { _light: 'teal.600', _dark: 'teal.400' },
          700: { _light: 'teal.700', _dark: 'teal.300' },
          800: { _light: 'teal.800', _dark: 'teal.200' },
          900: { _light: 'teal.900', _dark: 'teal.100' }
        },
        decrease: {
          100: { _light: 'red.100', _dark: 'red.900' },
          200: { _light: 'red.200', _dark: 'red.800' },
          300: { _light: 'red.300', _dark: 'red.700' },
          400: { _light: 'red.400', _dark: 'red.600' },
          500: { _light: 'red.500', _dark: 'red.500' },
          600: { _light: 'red.600', _dark: 'red.400' },
          700: { _light: 'red.700', _dark: 'red.300' },
          800: { _light: 'red.800', _dark: 'red.200' },
          900: { _light: 'red.900', _dark: 'red.100' }
        }
      }
    }
  },

  styles: {
    global: {
      '*': {
        fontFeatureSettings: '"cpsp", "ss04", "cv03", "cv04", "cv10", "calt", "ccmp", "kern"'
      },

      '.utility-icon': {
        color: 'utility-icon'
      },

      '.nav-scrollbar': {
        ...navScrollbar
      }
    }
  },

  components: {
    Accordion: accordionTheme,
    Alert: alertTheme,
    Avatar: avatarTheme,
    Badge: badgeTheme,
    Breadcrumb: breadcrumbTheme,
    Button: buttonTheme,
    Checkbox: checkboxTheme,
    Drawer: drawerTheme,
    FormLabel: formLabelTheme,
    Heading: headingTheme,
    Input: inputTheme,
    List: listTheme,
    Link: linkTheme,
    Menu: menuTheme,
    Modal: modalTheme,
    NumberInput: numberInputTheme,
    Popover: popoverTheme,
    Radio: radioTheme,
    Select: selectTheme,
    Textarea: textareaTheme,
    Table: tableTheme,
    Tabs: tabsTheme,
    Tag: tagTheme,
    Text: textTheme,
    Tooltip: tooltipTheme
  }
});
